import { useInfiniteQuery } from '@tanstack/react-query'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { transformContactsV3Response } from 'pages/Contacts/utils/utils'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getContacts, getContactsV3 } from 'services/ContactService'
import { Contact, ContactEntityType, Contacts, ContactsV3Response } from 'types/contacts'
import { API_PAGINATION_LIMIT } from 'utils/pagination'

interface ContactsInfiniteQueryProps {
  searchTerm?: string
  contactIds?: string[]
  type?: ContactEntityType
}

export const useContactsInfiniteQuery = ({
  searchTerm = '',
  contactIds = [],
  type,
}: ContactsInfiniteQueryProps) => {
  const { enableV3Contacts } = useFeatureFlags()

  const pageSize = API_PAGINATION_LIMIT

  const queryKey = enableV3Contacts
    ? reactQueryKeys.contactsV3Infinite({ searchQuery: searchTerm, size: pageSize })
    : reactQueryKeys.contactsPayment(searchTerm, contactIds, type)

  const queryFn = ({ pageParam = 0 }: { pageParam: number }) => {
    if (enableV3Contacts) {
      return getContactsV3({ size: pageSize, page: pageParam, searchQuery: searchTerm })
    }

    return getContacts({
      size: pageSize,
      page: pageParam,
      searchQuery: searchTerm,
      contactIds,
      type,
    })
  }

  const getNextPageParam = (lastPage: Contacts | ContactsV3Response) => {
    if (enableV3Contacts && 'hasNext' in lastPage) {
      return lastPage?.hasNext ? lastPage?.pageable?.pageNumber + 1 : undefined
    }

    if ('totalPages' in lastPage) {
      return lastPage?.pageable?.pageNumber + 1 < lastPage?.totalPages
        ? lastPage?.pageable.pageNumber + 1
        : undefined
    }

    return undefined
  }

  const mapPageContacts = (page: Contacts | ContactsV3Response): Contact[] => {
    if (enableV3Contacts && 'hasNext' in page) {
      return transformContactsV3Response(page)?.content
    }

    if ('content' in page) {
      return page?.content as Contact[]
    }

    return []
  }

  return useInfiniteQuery({
    queryKey,
    queryFn,
    getNextPageParam,
    initialPageParam: 0,
    select: data => {
      return data?.pages?.flatMap(mapPageContacts)
    },
  })
}
